import * as ArrayHelper from "helpers/arrayHelper";

export const DASH_PATH_DELIMITER = "|";
export const DASH_PATH_DELIMITER_UI = " / ";
export const DASH_VALUE_DELIMITER = "; ";

export interface IFilterKey { key: string, label: string }
export interface IFilterKeys { [key: string]: IFilterKey }

export type AddFilterBaseFunc<TFilterKey> = (key: keyof TFilterKey, value: string | string[] | number | number[], context?: FilterContext, labelOverride?: string) => void;
export type RemoveFilterBaseFunc<TFilterKey> = (key: keyof TFilterKey) => void;

/**
 * Filter path interface - the path is just an array of parts
 */
export interface IFilterPath {
    parts: string[];
}

/**
 * More complex filter
 */
export interface IFilter extends IFilterPath {
    labelOverride: string | undefined;
    value: string[];
}

/**
 * Generic base for MdtFilters for each dash type
 */
export type IFilters<T> = {
    [key in keyof T]?: IFilter
}

export class FilterContext implements IFilterPath {
    parts: string[];

    constructor(parts: string[] = []) {
        this.parts = [...parts];
    }

    /**
     * Returns true if there's a parent that can be synthesized, false otherwise.
     * E.g. Given a filter context: fc with path |a|b
     *        fc.hasParent() -> true
     * 
     *      Given a filter context: fc with an empty path |
     *        fc.hasParent() -> false
     */
    hasParent = (): boolean => this.parts.length > 0;

    /**
     * Synthesize a parent of the current context using the path.
     * E.g. Given a filter context: fc with path |a|b|[c1, c2, c3] 
     *      fc.parent() -> |a|b
     */
    parent = (): FilterContext | undefined => {
        if (this.hasParent()) {
            return new FilterContext(ArrayHelper.chopRight(this.parts, 1));
        }

        return undefined; 
    }

    /**
     * Returns a new a FilterContext from the path provided.
     * @param path The full path to build from (including values) in the form /a/b/c/[val1, val2] or /a/b/val
     */
    static fromPath = (path: string): FilterContext => {
        const parts = (path || "").split(DASH_PATH_DELIMITER).filter(p => p !== "");
        return new FilterContext(parts);
    }
    
    /**
     * Returns a new a FilterContext from the filter in the collection. A new empty FilterContext otherwise.
     * @param filters filters to search
     * @param filterKey key to find
     */
    static fromFilters = <TFilter extends IFilters<TFilterKey>, TFilterKey>(filters: TFilter, filterKey: keyof TFilterKey): FilterContext => {
        const filter: IFilter = filters[filterKey];
        return filter && new FilterContext(filter.parts) || new FilterContext();
    }

    /**
     * Synthesize a FilterContext from the position in the path. Zero based index.
     * E.g. Given a filter context: fc with path: /a/b/c
     *      fc.ancestor(0) -> /   (new FilterContext) 
     *      fc.ancestor(1) -> /a
     * 
     * @param depth where 0 is the root
     */
    ancestor = (depth: number): FilterContext => {
        const upToExcl = depth - 1;
        if (upToExcl < 0)
            return new FilterContext();

        return new FilterContext(this.parts.slice(0, upToExcl));
    }

    /**
     * Push the values to the parts array. If values has more than 1 element, 
     * it will be formatted as a multi-value.
     * 
     * E.g. Given a filter context: fc with path |a|b
     *      fc.push([c]) -> |a|b|c
     *      fc.push([c, d]) -> |a|b|[c, d]
     * 
     * @param values array of values to append to the path
     */
    push = (values: string[]) => {
        if (values.length === 0)
            return;

        this.parts.push(
            (values.length === 1)
                ? values[0]
                : ("[" + values.join(DASH_VALUE_DELIMITER) + "]")
        );
    }

    /**
     * Format the parts into a path string using DASH_PATH_DELIMITER.
     * E.g. Given a filter context: fc with parts == [a, b, c] 
     *      fc.toPath() -> |a|b|c
     */
    toPath = (): string => {
        let path = "";
        for (let j = 0; j < this.parts.length; j++) {
            path += DASH_PATH_DELIMITER + this.parts[j];
        }

        return path;
    }

    /**
     * Returns the path formatted for UI use.
     * E.g. Given a filter context: fc with the path |a|b|[c1, c2]
     *      context.pathLabel()
     * 
     * @param delimiter The delimiter to use when formatting the path label
     */
    pathLabel = (delimiter: string = ""): string => {
        if (!delimiter || delimiter === "") 
            delimiter = DASH_PATH_DELIMITER_UI;
            
        const parent = this.parent()
        const path = parent && parent.toPath() || "";
        let label = this.valuesLabel();
        
        label = path + DASH_PATH_DELIMITER + label;
        if (label.startsWith(DASH_PATH_DELIMITER)) {
            label = label.substring(1);
        }

        //Context might contain / so replace all at the end. Uses regex since string.replace only replaces one... :(
        return label.replace(new RegExp(`\\${DASH_PATH_DELIMITER}`, "gm"), delimiter);
    }

    /**
     * Returns the last element of the path into an array of one or more values.
     * 
     * E.g. Given a filter context: fc with the path |a|b|[c1, c2]
     *        fc.values() -> [c1, c2]
     * 
     *      Given a filter context: fc with the path |a|b|c
     *        fc.values() -> [c]
     */
    values = (): string[] => {
        if (this.parts.length === 0)
            return [];

        return FilterContext.valuesFromPart(ArrayHelper.lastOrDefault(this.parts, null, ""));
    }

    /**
     * Returns part transformed into an array of one or more values.
     * 
     * E.g. Given a filter context: fc with the path |a|b|[c1, c2] and we are checking the final part
     *        FilterContext.valuesFromPart("[c1, c2]") -> [c1, c2]
     * 
     *      Given a filter context: fc with the path |a|b|c and we are checking the final part
     *        FilterContext.valuesFromPart("c") -> [c]
     */
    static valuesFromPart(part: string): string[] {
        if (part.startsWith("[") && part.endsWith("]")) {
            return part.substring(1, part.length - 1).split(DASH_VALUE_DELIMITER);
        }

        return [part];
    }

    /**
     * Returns the last element of the path formatted for usage by the UI.
     * 
     * E.g. Given a filter context: fc with the path |a|b|[c1, c2]
     *        fc.values() -> "[c1, c2]""
     * 
     *      Given a filter context: fc with the path |a|b|c
     *        fc.values() -> "c"
     */
    valuesLabel = (): string => {
        const values = this.values();
        if (values.length === 0)
            return "";

        return (values.length === 1)
            ? values[0]
            : ("[" + values.join(", ") + "]");
    }
}
